import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { API, LOGO_URL } from "@/lib/api";
import { toast } from "sonner";
import { Search, X, Crown, Share2 } from "lucide-react";
import { ShareMenu } from "@/components/shop/ShareMenu";
import { CategoryTiles, IslamicStar } from "@/components/shop/CategoryTiles";
import { ProductCard } from "@/components/shop/ProductCard";
import { QuickView } from "@/components/shop/QuickView";
import { FlightBooking } from "@/components/shop/FlightBooking";
import { CollectionBanner } from "@/components/shop/CollectionBanner";
import { CATEGORIES, offPct } from "@/components/shop/categories";

const SORTS = [["featured", "Featured VIP"], ["low", "Price: Low to High"], ["high", "Price: High to Low"], ["off", "Highest Discount"], ["popular", "Most Popular"]];

const sorter = (s) => ({
  low: (a, b) => a.price - b.price, high: (a, b) => b.price - a.price, off: (a, b) => offPct(b) - offPct(a),
  popular: (a, b) => (b.clicks || 0) - (a.clicks || 0), featured: (a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0),
}[s]);

const SearchBar = ({ q, setQ, inputRef, big }) => (
  <div className={`relative w-full ${big ? "max-w-2xl" : "max-w-md"}`}>
    <Search className="absolute z-10 left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-[#D4AF37] pointer-events-none" />
    <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search prayer mats, attar, Quran, dates…" data-testid={big ? "shop-global-search-input" : "shop-top-search-input"}
      className={`w-full rounded-full bg-[#0B0F0D]/70 backdrop-blur-xl border border-[#D4AF37]/35 pl-14 pr-24 text-[#F5EFE6] placeholder:text-[#A3B8B0]/70 outline-none focus:border-[#D4AF37] focus:shadow-[0_0_22px_rgba(212,175,55,0.25)] transition-[border-color,box-shadow] ${big ? "h-14 text-base" : "h-11 text-sm"}`} />
    {q ? <button onClick={() => setQ("")} data-testid="shop-search-clear" className="absolute z-10 right-4 top-1/2 -translate-y-1/2 text-[#A3B8B0] hover:text-[#FFE9A8]"><X className="w-4 h-4" /></button>
      : <kbd className="hidden sm:block absolute right-5 top-1/2 -translate-y-1/2 text-[10px] font-mono text-[#A3B8B0] border border-[#D4AF37]/30 rounded px-1.5 py-0.5">Ctrl K</kbd>}
  </div>
);

export default function Shop() {
  const params = new URLSearchParams(window.location.search);
  const [items, setItems] = useState(null);
  const [q, setQ] = useState(params.get("q") || "");
  const [cat, setCat] = useState(params.get("cat") || "");
  const [sort, setSort] = useState("featured");
  const [view, setView] = useState(null);
  const [share, setShare] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    fetch(`${API}/shop/products`).then((r) => r.json()).then((d) => {
      setItems(d);
      const pid = new URLSearchParams(window.location.search).get("p");
      if (pid) setView(d.find((x) => x.id === pid) || null);
    }).catch(() => setItems([]));
  }, []);
  useEffect(() => {
    const k = (e) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); ref.current?.focus(); } };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);
  const counts = useMemo(() => (items || []).reduce((m, p) => ({ ...m, [p.category]: (m[p.category] || 0) + 1 }), {}), [items]);
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    const lbl = (k) => CATEGORIES.find((c) => c.key === k)?.label.toLowerCase() || "";
    return (items || []).filter((p) => (!cat || p.category === cat) && (!t || `${p.name} ${p.desc || ""} ${lbl(p.category)}`.toLowerCase().includes(t))).sort(sorter(sort));
  }, [items, q, cat, sort]);
  const buy = (p) => {
    if (p.sample) { toast.info("This is a sample item — the real product link will be added soon."); return; }
    window.open(`${API}/shop/go/${p.id}`, "_blank", "noopener,noreferrer");
  };
  return (
    <div className="min-h-screen bg-[#05261D] text-[#F5EFE6] font-sans" data-testid="shop-page">
      <div className="fixed inset-0 pointer-events-none opacity-[0.05] bg-[radial-gradient(circle_at_1px_1px,#D4AF37_1px,transparent_0)] [background-size:26px_26px]" />
      <header className="sticky top-0 z-30 bg-[#0B0F0D]/85 backdrop-blur-xl border-b border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center gap-4">
          <Link to="/showcase" className="flex items-center gap-2.5 flex-shrink-0" data-testid="shop-home-link">
            <img src={LOGO_URL} alt="Bengal Umrah House" className="w-9 h-9 rounded-full object-cover ring-1 ring-[#D4AF37]/50" />
            <span className="hidden sm:block leading-tight"><span className="block font-serif text-sm text-[#FFE9A8]">Bengal Umrah House</span><span className="block text-[9px] tracking-[0.25em] text-[#A3B8B0]">VIP COLLECTION</span></span>
          </Link>
          <div className="flex-1 flex justify-end"><SearchBar q={q} setQ={setQ} /></div>
          <button onClick={() => setShare({})} data-testid="shop-share-btn" className="inline-flex h-10 items-center gap-1.5 rounded-full px-4 text-xs font-bold text-[#05261D] bg-[linear-gradient(135deg,#D4AF37_0%,#F3E5AB_50%,#C5A059_100%)] hover:shadow-[0_0_18px_rgba(212,175,55,0.5)] transition-shadow"><Share2 className="w-4 h-4" /><span className="hidden sm:inline">Share Shop</span></button>
          <Link to="/" data-testid="shop-account-link" className="hidden sm:inline-flex h-10 items-center rounded-full border border-[#D4AF37]/40 px-4 text-xs font-semibold text-[#FFE9A8] hover:bg-[#D4AF37]/10">My Account</Link>
        </div>
      </header>
      <main className="relative max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <section className="relative overflow-hidden rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-r from-[#032B20] via-[#064E3B] to-[#0D382B] p-7 sm:p-12 lg:p-16 mb-10" data-testid="shop-hero">
          <IslamicStar className="absolute -right-16 -top-16 w-72 h-72 opacity-40 shop-spin" />
          <IslamicStar className="absolute right-24 bottom-[-70px] w-44 h-44 opacity-25" />
          <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-[#D4AF37] flex items-center gap-2"><Crown className="w-4 h-4" /> Royal Selection · Umrah & Sacred Essentials</p>
          <p className="font-arabic text-2xl sm:text-3xl text-[#FFE9A8] mt-4" dir="rtl">بِسْمِ ٱللَّٰهِ</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mt-2 max-w-3xl shop-shimmer">The Curated Umrah & Sacred Luxury Catalog</h1>
          <p className="text-sm sm:text-base text-[#A3B8B0] mt-4 max-w-xl leading-relaxed">Hand-picked prayer mats, royal attars, Ajwa dates, Holy Qurans and Islamic artisan craft — chosen for our pilgrims.</p>
          <div className="mt-8"><SearchBar q={q} setQ={setQ} inputRef={ref} big /></div>
        </section>
        <FlightBooking />
        <div className="flex items-end justify-between gap-4 mb-4">
          <div><p className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#D4AF37]">Shop by collection</p><h2 className="font-serif text-2xl sm:text-3xl mt-1">Sacred Collections</h2></div>
        </div>
        <CategoryTiles value={cat} onChange={setCat} counts={counts} />
        {cat && <CollectionBanner cat={cat} count={counts[cat] || 0} />}
        <div className="flex flex-wrap items-center justify-between gap-3 mt-8 mb-6">
          <p className="text-sm text-[#A3B8B0]" data-testid="shop-result-count">{items === null ? "Loading the collection…" : `${shown.length} ${shown.length === 1 ? "treasure" : "treasures"}${q ? ` for “${q}”` : ""}`}</p>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-select-dropdown" className="h-10 rounded-full bg-[#0B0F0D]/70 border border-[#D4AF37]/30 px-4 text-sm text-[#F5EFE6] outline-none">
            {SORTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-7" data-testid="shop-grid">
          {shown.map((p, i) => <ProductCard key={p.id} p={p} i={i} onBuy={buy} onView={setView} onShare={(x) => setShare(x)} />)}
        </div>
        {items !== null && shown.length === 0 && (
          <div className="text-center py-20" data-testid="shop-empty">
            <IslamicStar className="w-20 h-20 mx-auto opacity-70" />
            <p className="font-serif text-xl mt-4">Nothing found</p>
            <p className="text-sm text-[#A3B8B0] mt-1">Try another word or collection.</p>
          </div>
        )}
      </main>
      <footer className="relative border-t border-[#D4AF37]/15 py-8 text-center text-xs text-[#A3B8B0]">© {new Date().getFullYear()} Bengal Umrah House · VIP Collection</footer>
      <QuickView p={view} onClose={() => setView(null)} onBuy={buy} onShare={(x) => setShare(x)} />
      <ShareMenu open={!!share} onClose={() => setShare(null)} pid={share?.id} title={share?.name} />
    </div>
  );
}
